import { useState, useCallback } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import Checkbox from '@zenbusiness/zui/Checkbox';
import Tooltip from '@zenbusiness/zui/Tooltip';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import * as Styled from './SloganCard.style';

/**
 * @typedef {object} Slogan
 * @property {string} id - Unique identifier
 * @property {string} text - The slogan text
 * @property {string} [category] - Style category
 * @property {string} [tone] - Tone classification
 * @property {string} [insight] - Insight text explaining the slogan
 */

/**
 * SloganCard component for displaying individual slogan items
 * Matches Figma design with bordered card, large text, and simple category/tone display
 *
 * @param {object} props - Component props
 * @param {Slogan} props.slogan - Slogan data object
 * @param {boolean} [props.checked=false] - Checkbox state for multi-select
 * @param {Function} [props.onCheckChange] - Handler for checkbox change
 * @param {boolean} [props.showCopy=true] - Whether to show copy button
 * @param {string} [props.clickstreamElementName] - Clickstream element name for tracking
 * @component
 */
function SloganCard({
  slogan,
  checked = false,
  onCheckChange,
  showCopy = true,
  clickstreamElementName
}) {
  const [copied, setCopied] = useState(false);

  const handleCopyClick = useClickstreamCallback('linkPressed', {
    ...CLICKSTREAM_COMMON_PROPS,
    elementName: clickstreamElementName || 'slogan_card_copy',
    elementText: slogan.text
  });

  const handleCopy = useCallback(
    async (e) => {
      e.stopPropagation();
      handleCopyClick();

      try {
        await navigator.clipboard.writeText(slogan.text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy slogan:', err);
      }
    },
    [slogan.text, handleCopyClick]
  );

  const handleCheckboxChange = useCallback(
    (e) => {
      if (onCheckChange) {
        onCheckChange(slogan.id, e.target.checked);
      }
    },
    [onCheckChange, slogan.id]
  );

  // Build category/tone meta text
  const metaParts = [];
  if (slogan.category) {
    metaParts.push(`Category: ${slogan.category}`);
  }
  if (slogan.tone) {
    metaParts.push(`Tone: ${slogan.tone}`);
  }
  const metaText = metaParts.join(', ');

  return (
    <Styled.SloganCardRoot>
      <Styled.SloganContent>
        <Styled.SloganText>&ldquo;{slogan.text}&rdquo;</Styled.SloganText>
        {slogan.insight && <Styled.SloganInsight>{slogan.insight}</Styled.SloganInsight>}
        {metaText && <Styled.SloganMeta>{metaText}</Styled.SloganMeta>}
      </Styled.SloganContent>

      <Styled.ActionsContainer>
        {showCopy && (
          <Tooltip title={copied ? 'Copied!' : 'Copy slogan'}>
            <Styled.CopyButton onClick={handleCopy}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </Styled.CopyButton>
          </Tooltip>
        )}
        <Checkbox checked={checked} color="primary" size="medium" onChange={handleCheckboxChange} />
      </Styled.ActionsContainer>
    </Styled.SloganCardRoot>
  );
}

SloganCard.propTypes = {
  slogan: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    category: PropTypes.string,
    tone: PropTypes.string,
    insight: PropTypes.string
  }).isRequired,
  checked: PropTypes.bool,
  onCheckChange: PropTypes.func,
  showCopy: PropTypes.bool,
  clickstreamElementName: PropTypes.string
};

export default SloganCard;
